import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Coins, LogOut, Radar, ShieldCheck, User, Crown, Heart, Bell } from "lucide-react";
import { SocialButtons } from "@/components/Footer";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && user !== false;

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "text-emerald-400 bg-emerald-500/10" : "text-slate-300 hover:text-white hover:bg-slate-800/60"
    }`;

  return (
    <header className="sticky top-0 z-50 glass" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <div className="relative">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
              <Radar className="w-5 h-5 text-emerald-950" strokeWidth={2.5} />
            </div>
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse ring-2 ring-slate-950"></span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-white text-lg tracking-tight">LeilãoRadar</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 -mt-0.5">AI · Judicial</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {isAuthed && <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">Dashboard</NavLink>}
          <NavLink to="/search" className={linkClass} data-testid="nav-search">Pesquisar</NavLink>
          {isAuthed && <NavLink to="/favorites" className={linkClass} data-testid="nav-favorites">Favoritos</NavLink>}
          {isAuthed && <NavLink to="/alerts" className={linkClass} data-testid="nav-alerts">Alertas</NavLink>}
          <NavLink to="/auctioneers" className={linkClass} data-testid="nav-auctioneers">Leiloeiros</NavLink>
          {isAuthed && <NavLink to="/store" className={linkClass} data-testid="nav-store">Créditos</NavLink>}
          {isAuthed && user.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">Admin</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden xl:flex"><SocialButtons compact /></div>
          {isAuthed ? (
            <>
              {user.is_pro && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-br from-purple-500/25 to-fuchsia-500/15 border border-purple-500/40 text-purple-200" data-testid="pro-badge">
                  <Crown className="w-3.5 h-3.5" />
                  <span className="text-xs font-bold uppercase tracking-widest">Pro</span>
                </div>
              )}
              <Link
                to="/store"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-colors"
                data-testid="credit-balance-badge"
              >
                <Coins className="w-4 h-4" />
                <span className="font-mono font-bold text-sm">{user.is_pro ? "∞" : user.credits}</span>
                <span className="text-xs text-amber-200/70 hidden lg:inline">créditos</span>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 bg-slate-800/70 hover:bg-slate-700" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                  <DropdownMenuLabel>
                    <div className="text-xs text-slate-400">Logado como</div>
                    <div className="truncate text-sm">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites">
                    <Heart className="w-4 h-4 mr-2 text-red-400" />Favoritos
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/alerts")} data-testid="menu-alerts">
                    <Bell className="w-4 h-4 mr-2 text-emerald-400" />Alertas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/history")} data-testid="menu-history">Histórico</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/store")} data-testid="menu-store">Comprar Créditos</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <ShieldCheck className="w-4 h-4 mr-2 text-emerald-400" />Painel Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem onClick={logout} className="text-red-400" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-300" data-testid="header-login-btn">Entrar</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="header-register-btn">
                  Criar conta grátis
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
